import { useState, useMemo } from 'react';
import { ZoomIn, ZoomOut, Download, Printer, FileText, ChevronLeft, ChevronRight } from 'lucide-react';

interface ContractViewerProps {
  content: string;
  variables?: Record<string, string>;
  status?: 'DRAFT' | 'PENDING_SIGNATURES' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  showWatermark?: boolean;
  onDownload?: () => void;
  onPrint?: () => void;
  className?: string;
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'RASCUNHO',
  PENDING_SIGNATURES: 'AGUARDANDO ASSINATURAS',
  PARTIALLY_SIGNED: 'PARCIALMENTE ASSINADO',
  COMPLETED: 'ASSINADO',
  CANCELLED: 'CANCELADO',
  EXPIRED: 'EXPIRADO',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'text-gray-500',
  PENDING_SIGNATURES: 'text-yellow-500',
  PARTIALLY_SIGNED: 'text-blue-500',
  COMPLETED: 'text-green-500',
  CANCELLED: 'text-red-500',
  EXPIRED: 'text-red-500',
};

export function ContractViewer({
  content,
  variables = {},
  status = 'DRAFT',
  showWatermark = true,
  onDownload,
  onPrint,
  className = '',
}: ContractViewerProps) {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  // Replace variables in content
  const renderedContent = useMemo(() => {
    let result = content;
    
    // Replace {{variable}} patterns with custom variables
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, `<span class="bg-yellow-100 px-1 rounded">${value}</span>`);
    });

    // Highlight remaining unresolved system fields ({{categoria.campo}})
    result = result.replace(
      /\{\{([a-z_]+\.[a-z_]+)\}\}/g,
      '<span class="bg-orange-100 text-orange-700 px-1 rounded border border-orange-300 cursor-help" title="Campo de sistema não resolvido: $1">{{$1}}</span>'
    );

    // Highlight remaining unresolved custom variables ({{variavel}})
    result = result.replace(
      /\{\{([^.}]+)\}\}/g,
      '<span class="bg-red-100 text-red-600 px-1 rounded border border-red-300 cursor-help" title="Variável não preenchida: $1">{{$1}}</span>'
    );

    return result;
  }, [content, variables]);

  // Split content into pages (simplified - based on character count)
  const pages = useMemo(() => {
    const charsPerPage = 3000;
    const pageCount = Math.ceil(renderedContent.length / charsPerPage);
    const result: string[] = [];
    
    for (let i = 0; i < pageCount; i++) {
      result.push(renderedContent.slice(i * charsPerPage, (i + 1) * charsPerPage));
    }
    
    return result.length > 0 ? result : [renderedContent];
  }, [renderedContent]);

  const totalPages = pages.length;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className={`flex flex-col bg-gray-100 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-700">Visualizador de Contrato</span>
          {status && (
            <span className={`text-sm font-medium ${STATUS_COLORS[status]}`}>
              ({STATUS_LABELS[status]})
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="p-1 hover:bg-gray-100 rounded"
              title="Diminuir zoom"
            >
              <ZoomOut className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm text-gray-600 w-12 text-center">{zoom}%</span>
            <button
              onClick={handleZoomIn}
              className="p-1 hover:bg-gray-100 rounded"
              title="Aumentar zoom"
            >
              <ZoomIn className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Page navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 border-l pl-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm text-gray-600">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 border-l pl-4">
            {onPrint && (
              <button
                onClick={onPrint}
                className="p-1 hover:bg-gray-100 rounded"
                title="Imprimir"
              >
                <Printer className="w-4 h-4 text-gray-600" />
              </button>
            )}
            {onDownload && (
              <button
                onClick={onDownload}
                className="p-1 hover:bg-gray-100 rounded"
                title="Download PDF"
              >
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Document viewer */}
      <div className="flex-1 overflow-auto p-8 flex justify-center">
        <div
          className="relative bg-white shadow-lg"
          style={{
            width: `${(21 * zoom) / 100}cm`,
            minHeight: `${(29.7 * zoom) / 100}cm`,
            padding: `${(2 * zoom) / 100}cm`,
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
          }}
        >
          {/* Watermark */}
          {showWatermark && status !== 'COMPLETED' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              <span
                className={`text-6xl font-bold opacity-10 transform -rotate-45 ${STATUS_COLORS[status]}`}
                style={{ fontSize: `${3 * zoom / 100}rem` }}
              >
                {STATUS_LABELS[status]}
              </span>
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-sm max-w-none"
            style={{ fontSize: `${14 * zoom / 100}px` }}
            dangerouslySetInnerHTML={{ __html: pages[currentPage - 1] }}
          />

          {/* Page number */}
          <div className="absolute bottom-4 right-4 text-xs text-gray-400">
            Página {currentPage} de {totalPages}
          </div>
        </div>
      </div>
    </div>
  );
}
